import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {
  Card, Statistic, Icon, List,
  Label, Progress, Header, Modal, Loader,
} from 'semantic-ui-react';
import momentTZ from 'moment-timezone';
import { SEARCH_JOBS } from '../../queries/job';

import CompleteProjectButton from './CompleteProjectButton';
import DeleteProjectButton from './DeleteProjectButton';
import SearchHighlightSegment from '../SearchHighlightSegment';

const ProjectCard = ({ user, project, searchResult }) => {
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [fetchSearchData, { data: searchData, loading: searchLoading }] = useLazyQuery(
    SEARCH_JOBS,
    { fetchPolicy: 'network-only' },
  );

  const handleSearchClick = () => {
    setSearchModalOpen(true);
    fetchSearchData({
      variables: {
        projectId: project.id,
        query: searchResult.query,
        exclusions: [],
      },
    });
  };
  const renderProjectStats = () => {
    if (project.status === 'Completed') {
      return (
        <div className="complete-stats">
          <Progress percent={100} success size="small" />
          <div className="stats">
            <Icon name="check circle" />
            {`${project.numCompleted} completed`}
          </div>
        </div>
      );
    }
    return (
      <Statistic.Group size="tiny" widths={3}>
        {
          project.type === 'Interview' && (
            <Statistic>
              <Statistic.Value>{project.numUpcomingMeetings}</Statistic.Value>
              <Statistic.Label>Scheduled</Statistic.Label>
            </Statistic>
          )
        }
        <Statistic>
          <Statistic.Value>{project.numCompleted}</Statistic.Value>
          <Statistic.Label>Completed</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{project.numPendingInvites}</Statistic.Value>
          <Statistic.Label>Pending</Statistic.Label>
        </Statistic>
      </Statistic.Group>
    );
  };

  const renderStatus = () => {
    let status;
    if (project.qualtricsSurvey && project.qualtricsSurvey.status === 'New') {
      status = 'Unpublished';
    } else {
      status = project.status === 'Completed' ? 'Completed' : 'Active';
    }
    return (
      <span className={`${status}-status status`}>
        {status}
      </span>
    );
  };

  const renderTitle = () => {
    if (searchResult) {
      const titleHighlight = searchResult.highlights.find((highlight) => highlight.field === 'title');
      if (titleHighlight) {
        return <SearchHighlightSegment htmlContent={titleHighlight.segments[0]} />;
      }
    }
    return project.title;
  };

  const renderKeyword = (keyword) => {
    if (searchResult) {
      const keywordsHighlight = searchResult.highlights.find((highlight) => highlight.field === 'keywords');
      if (keywordsHighlight) {
        const highlightedKeywords = keywordsHighlight.segments.map((segment) => segment.replace(/<em>|<\/em>/g, ''));
        const highlightedKeyword = highlightedKeywords.find((k) => k === keyword);
        if (highlightedKeyword) {
          return <Label color="brown">{highlightedKeyword}</Label>;
        }
      }
    }
    return <Label>{keyword}</Label>;
  };

  return (
    <Card fluid className="project-card">
      <Card.Content>
        {renderStatus()}
        <Label size="tiny" basic className="project-type-label">{project.type}</Label>
        <Card.Meta>
          Last updated
          {' '}
          {momentTZ.tz(project.updatedAt, user.timeZone).format('MM/DD/YY')}
          . Project created
          {' '}
          {momentTZ.tz(project.createdAt, user.timeZone).format('MM/DD/YY')}
          .
        </Card.Meta>
        <Link to={`/projects/${project.id}`}>
          <Card.Header>
            <Header>{renderTitle()}</Header>
          </Card.Header>
          <Card.Meta>
            {project.owners.map((owner) => owner.email).join(', ')}
          </Card.Meta>
          <Card.Meta>
            <List horizontal>
              {project.keywords?.map((tag) => (
                <List.Item key={tag}>
                  {renderKeyword(tag)}
                </List.Item>
              ))}
            </List>
          </Card.Meta>
        </Link>
        <Card.Description>
          {renderProjectStats()}
        </Card.Description>
        <DeleteProjectButton project={project} />
        <CompleteProjectButton project={project} />
      </Card.Content>
      {searchResult && (
        <Card.Content extra>
          <List className="search-highlights">
            {searchResult.highlights.filter((highlight) => highlight.field === 'description').map((highlight) => (
              <List.Item key={highlight.field}>
                <div>
                  <strong>Project description: </strong>
                  <SearchHighlightSegment htmlContent={highlight.segments[0]} />
                </div>
              </List.Item>
            ))}
            {searchResult.instanceCount && (
              <List.Item>
                <Label circular color="brown" horizontal>{searchResult.instanceCount}</Label>
                <button
                  type="button"
                  onClick={handleSearchClick}
                  className="search-mention-button"
                >
                  {'Experts mention '}
                  <em>{searchResult.query}</em>
                </button>
              </List.Item>
            )}
          </List>
        </Card.Content>
      )}
      <Modal
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        size="large"
        closeIcon
      >
        <Modal.Header>
          {`Search Results for "${searchResult?.query}" in ${project.title}`}
        </Modal.Header>
        <Modal.Content>
          {searchLoading ? (
            <Loader active />
          ) : (
            searchData?.searchProjectData && (
              <div>
                <Header as="h4">
                  {`${searchData.searchProjectData.total} results found`}
                </Header>
                <List>
                  {searchData.searchProjectData.items.map((item) => (
                    <List.Item key={item.item.id}>
                      <div>
                        <Header as="h3">{item.item.user.fullName}</Header>
                        {item.highlights.map((highlight) => (
                          <div key={`${item.item.id}-${highlight.field}`}>
                            <SearchHighlightSegment
                              htmlContent={`<b>${highlight.field}:</b> ${highlight.segments.join(' ... ')}`}
                            />
                          </div>
                        ))}
                      </div>
                    </List.Item>
                  ))}
                </List>
              </div>
            )
          )}
        </Modal.Content>
      </Modal>
    </Card>
  );
};

export default ProjectCard;
